import { getApiBaseUrl } from '../config/config';

const API_BASE_URL = getApiBaseUrl();

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  status: number;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // If JSON parsing fails, try to get text
        const text = await response.text();
        return {
          error: `Invalid JSON response from server (${response.status}): ${text.substring(0, 200)}`,
          status: response.status,
        };
      }

      if (!response.ok) {
        const errorMessage = data.error || data.detail || data.message || `HTTP ${response.status}: ${response.statusText}`;
        const errorDetails = data.error_details ? `\n\nDetails:\n${data.error_details}` : '';
        const errorType = data.error_type ? `\nError Type: ${data.error_type}` : '';
        return {
          error: `${errorMessage}${errorType}${errorDetails}`,
          status: response.status,
          data: data, // Include full error data
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      // Network errors, CORS errors, etc.
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('API request failed:', {
        endpoint,
        error: errorMessage,
        errorType: error instanceof TypeError ? 'Network/CORS Error' : 'Unknown Error',
      });
      return {
        error: `Network error: ${errorMessage}. Check if the backend server is running and accessible.`,
        status: 0,
      };
    }
  }

  // Get all runs
  async getRuns(): Promise<any[]> {
    try {
      const response = await this.request('/api/runs');
      if (response.error) {
        throw new Error(response.error);
      }
      // Ensure we return an array
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      // If data is wrapped in another structure, try to extract it
      if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as any).data)) {
        return (data as any).data;
      }
      // If response.data is the array directly (sometimes the API returns it directly)
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      console.error('Error fetching runs:', error);
      return [];
    }
  }

  // Get specific run
  async getRun(runId: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}`);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Create new run
  async createNewRun(name?: string): Promise<any> {
    const response = await this.request('/api/runs/new', {
      method: 'POST',
      body: JSON.stringify({ name: name || '' }),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Get schema for a run
  async getSchema(runId: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/schema`);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Preview CSV file
  async previewCSV(runId: string, filename: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/csv/${filename}`);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Preview CSV from root
  async previewCSVFromRoot(filename: string): Promise<any> {
    const response = await this.request(`/api/csv/${filename}`);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Check dependencies
  async checkDependencies(runId: string, selectedTables: string[]): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/check-dependencies`, {
      method: 'POST',
      body: JSON.stringify({ selected_tables: selectedTables }),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Generate code
  async generateCode(runId: string, selectedTables: string[]): Promise<any> {
    try {
      const response = await this.request(`/api/runs/${runId}/generate-code`, {
        method: 'POST',
        body: JSON.stringify({ selected_tables: selectedTables }),
      });
      
      if (response.error) {
        // Include error details if available
        const errorData = response.data as any;
        
        // If error has structured data (quota, actionable steps, etc.), preserve it
        if (errorData && (errorData.error_type || errorData.actionable_steps)) {
          const structuredError = {
            message: response.error,
            error_type: errorData.error_type,
            details: errorData.details || errorData.error_details || '',
            actionable_steps: errorData.actionable_steps || []
          };
          throw new Error(JSON.stringify(structuredError));
        }
        
        const errorDetails = errorData?.error_details 
          ? `\n\nDetails:\n${errorData.error_details}` 
          : '';
        const errorType = errorData?.error_type 
          ? `\nError Type: ${errorData.error_type}` 
          : '';
        throw new Error(`${response.error}${errorType}${errorDetails}`);
      }
      
      if (!response.data) {
        throw new Error('No data returned from server');
      }
      
      return response.data;
    } catch (error) {
      // Re-throw with more context
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to generate code: ${String(error)}`);
    }
  }

  // Apply business rules
  async applyBusinessRules(runId: string, constraints: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/apply-business-rules`, {
      method: 'POST',
      body: JSON.stringify({ constraints }),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Generate data
  async generateData(runId: string, numRows: number = 500): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/generate-data`, {
      method: 'POST',
      body: JSON.stringify({ num_rows: numRows }),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Get full CSV data for editing (with pagination)
  async getFullCSV(runId: string, filename: string, page: number = 1, perPage: number = 500): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/csv/${filename}/full?page=${page}&per_page=${perPage}`);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Save edited CSV data
  async saveCSV(runId: string, filename: string, data: any[]): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/csv/${filename}/save`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Download single CSV file
  async downloadCSV(runId: string, filename: string): Promise<void> {
    const url = `${API_BASE_URL}/api/runs/${runId}/csv/${filename}/download`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Download all CSV files as zip
  async exportToDatabase(runId: string, connection: any): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/export-to-database`, {
      method: 'POST',
      body: JSON.stringify(connection),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  async downloadAllCSV(runId: string): Promise<void> {
    const url = `${API_BASE_URL}/api/runs/${runId}/csv/all/download`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to download files');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${runId}_synthetic_data.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Upload files to a run
  async uploadFiles(runId: string, formData: FormData): Promise<any> {
    try {
      const url = `${API_BASE_URL}/api/runs/${runId}/upload`;
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload files');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  // Analyze a run (trigger analyzer after upload)
  async analyzeRun(runId: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/analyze`, {
      method: 'POST',
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Get generator Python code
  async getGeneratorCode(runId: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/generator-code`);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Get column EDA
  async getColumnEDA(runId: string, tableName: string, columnName: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/column-eda/${tableName}/${columnName}`);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Test database connection
  async testDatabaseConnection(connection: {
    type: string;
    host: string;
    port: string;
    database: string;
    username: string;
    password: string;
    schema?: string;
  }): Promise<any> {
    const response = await this.request('/api/database/test', {
      method: 'POST',
      body: JSON.stringify(connection),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Import from database
  async importFromDatabase(connection: {
    type: string;
    host: string;
    port: string;
    database: string;
    username: string;
    password: string;
    schema?: string;
    name?: string;
  }): Promise<any> {
    const response = await this.request('/api/database/import', {
      method: 'POST',
      body: JSON.stringify(connection),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Delete a run
  async deleteRun(runId: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}`, {
      method: 'DELETE',
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }

  // Get AI-powered business rules suggestions
  async getBusinessRulesSuggestions(runId: string, purpose?: string): Promise<any> {
    const response = await this.request(`/api/runs/${runId}/suggest-rules`, {
      method: 'POST',
      body: JSON.stringify({ purpose }),
    });
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data;
  }
}

export const apiService = new ApiService();

